const DESKTOP_FINE_POINTER_QUERY = "(min-width: 992px) and (hover: hover) and (pointer: fine)";
const DRAG_THRESHOLD = 4;
const OVERFLOW_TOLERANCE = 2;
const HORIZONTAL_SCROLL_OVERFLOW_VALUES = /* @__PURE__ */ new Set([
  "auto",
  "scroll",
  "overlay"
]);
class HorizontalDragScroll extends HTMLElement {
  constructor() {
    super();
    this._onMediaChange = this._onMediaChange.bind(this);
    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerEnter = this._onPointerEnter.bind(this);
    this._onPointerLeave = this._onPointerLeave.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerEnd = this._onPointerEnd.bind(this);
    this._onClick = this._onClick.bind(this);
    this._onDragStart = this._onDragStart.bind(this);
    this._refreshOverflow = this._refreshOverflow.bind(this);
    this._scheduleOverflowRefresh = this._scheduleOverflowRefresh.bind(this);
  }
  connectedCallback() {
    this._scrollElement = this.dataset.scrollTarget === "parent" ? this.parentElement : this;
    if (!this._scrollElement) return;
    this._mediaQuery = window.matchMedia(DESKTOP_FINE_POINTER_QUERY);
    this._mediaQuery.addEventListener("change", this._onMediaChange);
    this._resizeObserver = new ResizeObserver(this._scheduleOverflowRefresh);
    this._resizeObserver.observe(this._scrollElement);
    this._observeScrollChildren();
    this._mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.removedNodes.forEach((node) => this._unobserveResizeTarget(node));
        mutation.addedNodes.forEach((node) => this._observeResizeTarget(node));
      });
      this._scheduleOverflowRefresh();
    });
    this._mutationObserver.observe(this._scrollElement, { childList: true });
    this._onMediaChange(this._mediaQuery);
  }
  disconnectedCallback() {
    var _a, _b, _c;
    this._disable();
    this._setScrollable(false);
    (_a = this._resizeObserver) == null ? void 0 : _a.disconnect();
    this._resizeObserver = null;
    (_b = this._mutationObserver) == null ? void 0 : _b.disconnect();
    this._mutationObserver = null;
    (_c = this._mediaQuery) == null ? void 0 : _c.removeEventListener("change", this._onMediaChange);
    this._mediaQuery = null;
    clearTimeout(this._clickResetTimer);
    this._clickResetTimer = null;
    cancelAnimationFrame(this._overflowRefreshFrame);
    this._overflowRefreshFrame = null;
    this._suppressClick = false;
    this._scrollElement = null;
  }
  _onMediaChange(event) {
    if (event.matches) {
      this._enable();
      this._refreshOverflow();
    } else {
      this._disable();
      this._setScrollable(false);
    }
  }
  _enable() {
    if (this._enabled || !this._scrollElement) return;
    this._enabled = true;
    this._scrollElement.addEventListener("pointerenter", this._onPointerEnter);
    this._scrollElement.addEventListener("pointerleave", this._onPointerLeave);
    this._scrollElement.addEventListener("pointerdown", this._onPointerDown);
    this._scrollElement.addEventListener("pointermove", this._onPointerMove);
    this._scrollElement.addEventListener("pointerup", this._onPointerEnd);
    this._scrollElement.addEventListener("pointercancel", this._onPointerEnd);
    this._scrollElement.addEventListener("lostpointercapture", this._onPointerEnd);
    this._scrollElement.addEventListener("click", this._onClick, true);
    this._scrollElement.addEventListener("dragstart", this._onDragStart);
  }
  _disable() {
    if (!this._enabled || !this._scrollElement) return;
    this._enabled = false;
    this._scrollElement.removeEventListener("pointerenter", this._onPointerEnter);
    this._scrollElement.removeEventListener("pointerleave", this._onPointerLeave);
    this._scrollElement.removeEventListener("pointerdown", this._onPointerDown);
    this._scrollElement.removeEventListener("pointermove", this._onPointerMove);
    this._scrollElement.removeEventListener("pointerup", this._onPointerEnd);
    this._scrollElement.removeEventListener("pointercancel", this._onPointerEnd);
    this._scrollElement.removeEventListener("lostpointercapture", this._onPointerEnd);
    this._scrollElement.removeEventListener("click", this._onClick, true);
    this._scrollElement.removeEventListener("dragstart", this._onDragStart);
    this._resetPointer();
  }
  _refreshOverflow() {
    const measurement = this._measureOverflow();
    if (measurement) this._applyOverflowMeasurement(measurement);
  }
  _scheduleOverflowRefresh() {
    if (this._overflowRefreshFrame) return;
    this._overflowRefreshFrame = requestAnimationFrame(() => {
      this._overflowRefreshFrame = null;
      this._refreshOverflow();
    });
  }
  _measureOverflow() {
    var _a;
    const scrollElement = this._scrollElement;
    if (!this.isConnected || !(scrollElement == null ? void 0 : scrollElement.isConnected) || !((_a = this._mediaQuery) == null ? void 0 : _a.matches)) return null;
    const computedStyle = getComputedStyle(scrollElement);
    const allowsHorizontalScroll = HORIZONTAL_SCROLL_OVERFLOW_VALUES.has(computedStyle.overflowX);
    return {
      scrollElement,
      rtlMultiplier: computedStyle.direction === "rtl" ? -1 : 1,
      isScrollable: allowsHorizontalScroll && scrollElement.scrollWidth > scrollElement.clientWidth + OVERFLOW_TOLERANCE
    };
  }
  _applyOverflowMeasurement({ scrollElement, rtlMultiplier, isScrollable }) {
    var _a;
    if (scrollElement !== this._scrollElement || !scrollElement.isConnected || !((_a = this._mediaQuery) == null ? void 0 : _a.matches)) return;
    this._rtlMultiplier = rtlMultiplier;
    this._setScrollable(isScrollable);
  }
  _setScrollable(isScrollable) {
    if (!this._scrollElement) return;
    if (this._isScrollable === isScrollable) return;
    this._isScrollable = isScrollable;
    this._scrollElement.classList.toggle("is-horizontal-drag-scrollable", isScrollable);
    if (!isScrollable) this._resetPointer();
  }
  _onPointerEnter() {
    this._refreshOverflow();
  }
  _onPointerLeave(event) {
    if (!this._dragging && event.pointerId === this._pointerId) this._resetPointer();
  }
  _observeScrollChildren() {
    Array.from(this._scrollElement.children).forEach((child) => this._observeResizeTarget(child));
  }
  _observeResizeTarget(node) {
    var _a;
    if (node.nodeType !== Node.ELEMENT_NODE || node === this || node.tagName === "SCROLL-SHADOW") {
      return;
    }
    (_a = this._resizeObserver) == null ? void 0 : _a.observe(node);
  }
  _unobserveResizeTarget(node) {
    var _a;
    if (node.nodeType === Node.ELEMENT_NODE) (_a = this._resizeObserver) == null ? void 0 : _a.unobserve(node);
  }
  _onPointerDown(event) {
    var _a, _b;
    if (!this._isScrollable || event.button !== 0 || event.pointerType !== "mouse" || ((_b = (_a = event.target).closest) == null ? void 0 : _b.call(_a, "[data-no-drag]"))) {
      return;
    }
    this._pointerId = event.pointerId;
    this._startX = event.clientX;
    this._startY = event.clientY;
    this._startScrollLeft = this._scrollElement.scrollLeft;
    this._dragging = false;
    this._scrollElement.classList.add("is-horizontal-drag-pending");
  }
  _onPointerMove(event) {
    if (event.pointerId !== this._pointerId) return;
    if ((event.buttons & 1) === 0) {
      this._resetPointer();
      return;
    }
    const deltaX = event.clientX - this._startX;
    const deltaY = event.clientY - this._startY;
    if (!this._dragging) {
      if (Math.abs(deltaX) < DRAG_THRESHOLD && Math.abs(deltaY) < DRAG_THRESHOLD) return;
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        this._resetPointer();
        return;
      }
      this._dragging = true;
      this._scrollElement.classList.replace("is-horizontal-drag-pending", "is-horizontal-dragging");
      this._scrollElement.setPointerCapture(event.pointerId);
    }
    if (event.cancelable) event.preventDefault();
    this._scrollElement.scrollLeft = this._startScrollLeft - deltaX * this._rtlMultiplier;
  }
  _onPointerEnd(event) {
    if (event.pointerId !== this._pointerId) return;
    const didDrag = this._dragging;
    const pointerId = this._pointerId;
    this._resetPointer();
    if (this._scrollElement.hasPointerCapture(pointerId)) {
      this._scrollElement.releasePointerCapture(pointerId);
    }
    if (didDrag && event.type !== "pointercancel") {
      this._suppressClick = true;
      clearTimeout(this._clickResetTimer);
      this._clickResetTimer = setTimeout(() => {
        this._suppressClick = false;
      }, 0);
    }
  }
  _resetPointer() {
    var _a;
    this._pointerId = null;
    this._dragging = false;
    (_a = this._scrollElement) == null ? void 0 : _a.classList.remove("is-horizontal-drag-pending", "is-horizontal-dragging");
  }
  _onClick(event) {
    if (!this._suppressClick) return;
    this._suppressClick = false;
    event.preventDefault();
    event.stopImmediatePropagation();
  }
  _onDragStart(event) {
    if (this._isScrollable) event.preventDefault();
  }
}
if (!customElements.get("horizontal-drag-scroll")) {
  customElements.define("horizontal-drag-scroll", HorizontalDragScroll);
}
